"use client"

import { useState } from "react"
import { Download, FileText, ImageIcon, AlertTriangle } from "lucide-react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import type { ArtifactData, ArtifactEvent } from "./types"
import { ImageLightbox } from "@/components/ai-elements/image-lightbox"
import { ChartLightbox } from "@/components/ai-elements/chart-lightbox"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart"

// Raster image formats that we can render inline as <img>. Anything else
// (html/pdf/pptx/xlsx) falls back to a download card.
const IMAGE_FMTS = new Set(["png", "jpg", "jpeg", "webp", "gif"])

/**
 * Normalize an all-uppercase label (e.g. "CONCIERTOS", "TEATRO Y CULTURA") to
 * Title Case ("Conciertos", "Teatro Y Cultura"). Labels that already contain
 * lowercase (e.g. "Bad Bunny") are left untouched, so mixed-case data from the
 * agent is preserved. Small connector words are kept lowercase.
 */
const MINOR_WORDS = new Set(["y", "de", "del", "la", "el", "los", "las", "en", "a"])
function titleizeIfUpper(label: string): string {
  if (!label || label !== label.toUpperCase() || !/[A-ZÁÉÍÓÚÑ]/.test(label)) {
    return label
  }
  return label
    .toLocaleLowerCase("es-MX")
    .split(" ")
    .map((word, i) =>
      i > 0 && MINOR_WORDS.has(word) ? word : word.charAt(0).toLocaleUpperCase("es-MX") + word.slice(1),
    )
    .join(" ")
}

/**
 * Renders a chart artifact that has `data` but no PNG.
 *
 * Bar charts are rendered with Recharts using design tokens from globals.css
 * (`--chart-1`, `--chart-2`, …). Other `kind` values fall back to a shadcn Table.
 */
function DataOnlyChartCard({
  title,
  data,
  note,
}: Readonly<{
  title: string
  data: ArtifactData
  note: React.ReactNode
}>) {
  const columns = Array.isArray(data.columns) ? data.columns : []
  const rows = Array.isArray(data.rows) ? data.rows : []
  const kind = data.kind

  // Build a flat object array Recharts can consume: { [col]: value, … }
  const chartData = rows.map(row => {
    const cells = Array.isArray(row) ? row : []
    const obj: Record<string, string | number | null> = {}
    columns.forEach((col, i) => {
      obj[col] = cells[i] ?? null
    })
    return obj
  })

  // Series columns = everything except the first (category) column.
  const xKey = columns[0] ?? "label"
  const seriesKeys = columns.slice(1)

  // Sanitize column names for CSS variable compatibility (no spaces/parens).
  const sanitizeKey = (k: string) => k.replace(/\s+/g, "-").replace(/[()]/g, "")

  // Remap data to use sanitized keys so Recharts dataKey matches the config.
  const sanitizedSeriesKeys = seriesKeys.map(sanitizeKey)
  const sanitizedXKey = sanitizeKey(xKey)
  const sanitizedChartData = chartData.map(row => {
    const obj: Record<string, string | number | null> = {}
    for (const [k, v] of Object.entries(row)) {
      const key = sanitizeKey(k)
      // Titleize the categorical X-axis label (e.g. "CONCIERTOS" -> "Conciertos");
      // numeric series values are left as-is.
      obj[key] = key === sanitizedXKey && typeof v === "string" ? titleizeIfUpper(v) : v
    }
    return obj
  })

  // Build a ChartConfig from the sanitized keys, mapping to --chart-N tokens.
  const chartConfig: ChartConfig = Object.fromEntries(
    seriesKeys.map((key, i) => [
      sanitizeKey(key),
      { label: key, color: `var(--chart-${i + 1})` },
    ])
  )

  const chartKind = kind ?? "table"

  // ── PIE: different data shape — 2 columns: label + value ────────────────
  if (chartKind === "pie") {
    const labelKey = columns[0] ?? "label"
    const valueKey = columns[1] ?? "value"
    // Distinct tones for pie slices so no two adjacent slices share a hue. The
    // brand palette has two reds and two blacks; this order keeps them apart.
    const pieColors = [
      "var(--chart-1)", // brand red
      "var(--chart-2)", // near-black
      "var(--chart-3)", // mid gray
      "var(--chart-5)", // dark gray
      "var(--chart-4)", // accent red (kept far from chart-1)
    ]
    const pieData = rows.map((row, i) => {
      const cells = Array.isArray(row) ? row : []
      return {
        name: titleizeIfUpper(String(cells[0] ?? `slice-${i}`)),
        value: Number(cells[1] ?? 0),
        fill: pieColors[i % pieColors.length],
      }
    })
    // Index the config by the slice `name` (not a sanitized key) so the legend
    // and tooltip — which look up by nameKey="name" — resolve every slice's
    // label and color. A mismatched key was leaving one legend dot blank.
    const pieConfig: ChartConfig = Object.fromEntries(
      pieData.map((d) => [d.name, { label: d.name, color: d.fill }])
    )
    const total = pieData.reduce((s, d) => s + d.value, 0)

    const pieChart = (minHeight: string) => (
      <ChartContainer config={pieConfig} className={`${minHeight} w-full`}>
        <PieChart accessibilityLayer>
          <ChartTooltip
            content={
              <ChartTooltipContent
                nameKey="name"
                formatter={(value, name) => {
                  const pct = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : "0"
                  return [`${Number(value).toLocaleString("es-MX")} (${pct}%)`, name]
                }}
              />
            }
          />
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="80%"
            label={({ name, value }) => {
              const pct = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : "0"
              return `${name}: ${pct}%`
            }}
            labelLine={true}
          >
            {pieData.map((entry, i) => (
              <Cell key={`${labelKey}-${valueKey}-${i}`} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
    )

    return (
      <div
        className="my-2 overflow-hidden rounded-chart-card border border-chart-card-border bg-chart-card"
        data-testid="artifact-plot"
        data-artifact-status="rendered"
        data-artifact-render="chart"
      >
        <div className="flex items-center px-3 pt-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{title}</span>
        </div>
        <div className="px-3 py-3">
          <ChartLightbox title={title} expanded={pieChart("min-h-[70vh]")}>
            {pieChart("min-h-[260px]")}
          </ChartLightbox>
        </div>
        {note}
      </div>
    )
  }

  // ── BAR / LINE / AREA: shared axes layout ───────────────────────────────
  const isCartesian = chartKind === "bar" || chartKind === "line" || chartKind === "area"
  const hasCartesianSeries = isCartesian && seriesKeys.length > 0

  // Recharts' default Y-axis width (60px) truncates long formatted labels
  // (e.g. "2,000,000"). Size the axis to the widest formatted tick so the
  // leading digit is never clipped. ~7px per char + padding is enough for the
  // chart's xs font; clamp to a sane range.
  const formatTick = (v: number) => v.toLocaleString("es-MX")
  const yAxisWidth = (() => {
    let maxLen = 0
    for (const row of sanitizedChartData) {
      for (const key of sanitizedSeriesKeys) {
        const val = row[key]
        if (typeof val === "number") {
          maxLen = Math.max(maxLen, formatTick(val).length)
        }
      }
    }
    return Math.min(96, Math.max(40, maxLen * 8 + 12))
  })()

  // With many categories or long labels (e.g. 15 artist names) Recharts hides
  // overlapping X-axis ticks by default, so the user sees fewer labels than
  // bars. When the axis is crowded, force every tick to render (interval 0),
  // rotate them so they fit, and give the axis extra bottom room + height.
  const xLabels = sanitizedChartData.map(row => String(row[sanitizedXKey] ?? ""))
  const longestXLabel = xLabels.reduce((m, l) => Math.max(m, l.length), 0)
  const crowdedXAxis = xLabels.length > 6 || longestXLabel > 8
  const xAxisProps = crowdedXAxis
    ? {
        interval: 0 as const,
        angle: -45,
        textAnchor: "end" as const,
        height: 64,
      }
    : {}
  const chartMinHeight = crowdedXAxis ? "min-h-[320px]" : "min-h-[260px]"
  const chartMargin = { top: 4, right: 8, left: 0, bottom: crowdedXAxis ? 24 : 4 }

  // A legend with colored dots only helps when there are several series to tell
  // apart (e.g. 2024 vs 2025). A single-series bar chart would show one dot, so
  // we omit it there.
  const hasMultipleSeries = sanitizedSeriesKeys.length > 1
  const legend = hasMultipleSeries ? (
    <ChartLegend verticalAlign="top" content={<ChartLegendContent />} />
  ) : null

  const cartesianChart = (minHeight: string) => (
    <ChartContainer config={chartConfig} className={`${minHeight} w-full`}>
      {chartKind === "bar" ? (
        <BarChart accessibilityLayer data={sanitizedChartData} margin={chartMargin}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey={sanitizedXKey} tickLine={false} tickMargin={10} axisLine={false} {...xAxisProps} />
          <YAxis axisLine={false} tickLine={false} width={yAxisWidth} tickFormatter={formatTick} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {legend}
          {sanitizedSeriesKeys.map((key) => (
            <Bar key={key} dataKey={key} fill={`var(--color-${key})`} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      ) : chartKind === "line" ? (
        <LineChart accessibilityLayer data={sanitizedChartData} margin={chartMargin}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey={sanitizedXKey} tickLine={false} tickMargin={10} axisLine={false} {...xAxisProps} />
          <YAxis axisLine={false} tickLine={false} width={yAxisWidth} tickFormatter={formatTick} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {legend}
          {sanitizedSeriesKeys.map((key) => (
            <Line key={key} dataKey={key} stroke={`var(--color-${key})`} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      ) : (
        <AreaChart accessibilityLayer data={sanitizedChartData} margin={chartMargin}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey={sanitizedXKey} tickLine={false} tickMargin={10} axisLine={false} {...xAxisProps} />
          <YAxis axisLine={false} tickLine={false} width={yAxisWidth} tickFormatter={formatTick} />
          <ChartTooltip content={<ChartTooltipContent />} />
          {legend}
          {sanitizedSeriesKeys.map((key) => (
            <Area
              key={key}
              dataKey={key}
              stroke={`var(--color-${key})`}
              fill={`var(--color-${key})`}
              fillOpacity={0.2}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </AreaChart>
      )}
    </ChartContainer>
  )

  return (
    <div
      className="my-2 overflow-hidden rounded-chart-card border border-chart-card-border bg-chart-card"
      data-testid="artifact-plot"
      data-artifact-status="rendered"
      data-artifact-render={hasCartesianSeries ? "chart" : "data"}
    >
      <div className="flex items-center px-3 pt-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{title}</span>
      </div>

      {hasCartesianSeries ? (
        <div className="px-3 py-3">
          <ChartLightbox title={title} expanded={cartesianChart("min-h-[70vh]")}>
            {cartesianChart(chartMinHeight)}
          </ChartLightbox>
        </div>
      ) : (
        <div className="max-h-72 overflow-auto px-3 py-2">
          <Table>
            {columns.length > 0 && (
              <TableHeader className="bg-muted">
                <TableRow>
                  {columns.map(col => (
                    <TableHead key={col} className="border-r border-border last:border-r-0 text-xs px-2 py-1.5">
                      {col}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
            )}
            <TableBody>
              {rows.map((row, r) => {
                const cells = Array.isArray(row) ? row : []
                const rowKey = `${String(cells[0] ?? "")}-${r}`
                return (
                  <TableRow key={rowKey}>
                    {cells.map((cell, c) => (
                      <TableCell
                        key={`${rowKey}-${columns[c] ?? c}`}
                        className="border-r border-border last:border-r-0 text-xs px-2 py-1"
                      >
                        {typeof cell === "number" ? cell.toLocaleString("es-MX") : String(cell ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {note}
    </div>
  )
}

function formatBytes(bytes?: number): string | null {
  if (bytes == null || bytes <= 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface ArtifactCardProps {
  artifact: ArtifactEvent
  /**
   * Optional click-time resolver for a FRESH presigned download URL. When
   * provided (reopened cloud conversations), the download control fetches a
   * short-lived URL on click instead of using the embedded `url` (which may
   * have expired). On any failure it falls back to the embedded `url`.
   */
  onResolveDownloadUrl?: () => Promise<string>
}

export function ArtifactCard({ artifact, onResolveDownloadUrl }: ArtifactCardProps) {
  const { fmt, url, name, bytes, kind, backend, intendedBackend } = artifact
  const [status, setStatus] = useState<"loading" | "rendered" | "error">(
    IMAGE_FMTS.has(fmt.toLowerCase()) && url ? "loading" : "rendered"
  )

  const isImage = IMAGE_FMTS.has(fmt.toLowerCase())
  const displayName = name || `${kind}.${fmt}`
  const sizeLabel = formatBytes(bytes)

  // `data` crosses an LLM-authored boundary (the sandbox code is model-written), so
  // validate the shape instead of trusting it: a malformed payload must degrade to
  // the download card, never throw inside render.
  const hasTabularData =
    Array.isArray(artifact.data?.rows) && artifact.data.rows.length > 0

  // Download click: when a resolver is available, mint a FRESH presigned URL at
  // click time and download it by navigating the CURRENT tab. The S3 object is
  // served as binary/octet-stream with no Content-Disposition, so assigning
  // window.location.href triggers a download rather than a real navigation — the
  // chat page stays put. No tab is opened (nothing to orphan) and window.open is
  // never called (no popup for Safari/Firefox to block), so the async await no
  // longer matters: navigating the current tab needs no user-activation token.
  // On resolver failure we fall back to the embedded url the same way. When no
  // resolver is provided (local build / signed out) handleDownload is undefined
  // and the plain <a href download> default is used unchanged.
  const handleDownload = onResolveDownloadUrl
    ? (e: React.MouseEvent) => {
        e.preventDefault()
        onResolveDownloadUrl()
          .then(fresh => {
            window.location.href = fresh
          })
          .catch(() => {
            if (url) window.location.href = url
          })
      }
    : undefined

  // An s3->local fallback means the link is a file:// URL only valid on the
  // machine that produced it — not shareable. Mirror deliver()'s warning.
  const degraded = intendedBackend === "s3" && backend === "local"

  const degradedNote = degraded ? (
    <div className="mt-1.5 flex items-start gap-1.5 rounded border border-warn/30 bg-warn/10 px-2 py-1 text-xs text-warn">
      <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>Enlace local (file://): no es compartible. Se esperaba almacenamiento en S3.</span>
    </div>
  ) : null

  // Interactive chart takes precedence: whenever the artifact carries tabular
  // `data`, render the interactive Recharts chart — NEVER the PNG. The backend
  // still emits a PNG today for compatibility, but that image path is being
  // removed, so a graphable artifact must always render as the live chart
  // regardless of whether a `url` (PNG) is present.
  if (hasTabularData) {
    return (
      <DataOnlyChartCard title={displayName} data={artifact.data!} note={degradedNote} />
    )
  }

  // Inline image render (happy path: presigned S3 PNG). Click to expand to a
  // full-screen viewer with download. Only reached for real images — artifacts
  // with no tabular data (photos, diagrams), not charts.
  if (isImage && url && status !== "error") {
    return (
      <div className="my-2" data-testid="artifact-plot" data-artifact-status={status}>
        <ImageLightbox
          src={url}
          alt={displayName}
          downloadName={displayName}
          thumbnailClassName="my-2"
          onLoad={() => setStatus("rendered")}
          onError={() => setStatus("error")}
        />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ImageIcon size={12} className="shrink-0" aria-hidden="true" />
          <span className="truncate">{displayName}</span>
          {sizeLabel && <span className="text-muted-foreground/70">· {sizeLabel}</span>}
          <a
            href={url}
            download={displayName}
            onClick={handleDownload}
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-brand-info hover:text-brand-info/80 hover:underline"
          >
            <Download size={12} aria-hidden="true" />
            Descargar
          </a>
        </div>
        {degradedNote}
      </div>
    )
  }

  // Download card: non-image formats, missing url, or an image that failed to
  // load. Always keeps a download/open affordance when a url is present.
  return (
    <div
      className="my-2 flex items-center gap-2.5 rounded border border-border bg-muted px-3 py-2 text-sm"
      data-testid="artifact-plot"
      data-artifact-status={status === "error" ? "error" : "rendered"}
    >
      <FileText size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{displayName}</div>
        <div className="text-xs uppercase text-muted-foreground">
          {fmt}
          {sizeLabel && <span className="normal-case"> · {sizeLabel}</span>}
          {status === "error" && (
            <span className="normal-case text-warn"> · no se pudo mostrar la imagen</span>
          )}
        </div>
        {degradedNote}
      </div>
      {url && (
        <a
          href={url}
          download={displayName}
          onClick={handleDownload}
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-brand-info hover:text-brand-info/80 hover:underline"
        >
          <Download size={14} aria-hidden="true" />
          {isImage ? "Descargar" : "Abrir"}
        </a>
      )}
    </div>
  )
}
